import {
	createContext,
	Dispatch,
	ReactNode,
	Reducer,
	useContext,
	useEffect,
	useReducer,
} from "react";
import { useInterval } from "react-use";
import { getPersonAssignments, PersonAssignmentRetrieveType } from "../../api";
import {
	Assignment as AssignmentType,
	getAssignments,
} from "../../api/assignments/assignment";
import {
	AssignmentBoardConfigResponse,
	getAssignmentBoardConfigs,
} from "../../api/assignments/assignment-board-config";
import { Company as CompanyType, getCompaniesForIds } from "../../api/company/company";
import { skipFailedPollTick, skipTransientApiErrors } from "../../api/fetch";
import { BoardColumnType } from "./board-layout-config";

const ONE_HOUR_IN_MILLISECONDS = 1000 * 60 * 60;

const assignmentSort = (a: AssignmentType, b: AssignmentType) => {
	if (a.name < b.name) return -1;
	if (a.name > b.name) return 1;
	return 0;
};

export const assignmentFilter = (a: AssignmentType) => {
	// return (
	// 	a.status === AssignmentStatus.ACTIVE ||
	// 	a.status === AssignmentStatus.PREPARATION
	// );
	return true;
};

const companySort = (a: CompanyType, b: CompanyType) => {
	if (a.name < b.name) return -1;
	if (a.name > b.name) return 1;
	return 0;
};

const personAssignmentSort = (
	a: PersonAssignmentRetrieveType,
	b: PersonAssignmentRetrieveType
) => {
	return new Date(b.created).getTime() - new Date(a.created).getTime();
};

const personAssignmentFilter = (a: PersonAssignmentRetrieveType) => {
	// return a.archived === false || a.archived === null;
	return true;
};

export type BoardState = {
	loading?: boolean;
	companies?: CompanyType[];
	assignments?: AssignmentType[];
	personAssignments?: { [key: number]: PersonAssignmentRetrieveType[] };
	assignmentBoardConfigs?: AssignmentBoardConfigResponse[];
};

export type BoardStateData = BoardState & {
	column?: BoardColumnType;
	assignment?: AssignmentType;
	oldPersonAssignment?: PersonAssignmentRetrieveType;
	newPersonAssignment?: PersonAssignmentRetrieveType;
	newAssignmentBoardConfig?: AssignmentBoardConfigResponse;
	oldAssignmentBoardConfig?: AssignmentBoardConfigResponse;
};

const initialBoardState: BoardState = {
	loading: true,
	companies: [],
	assignments: [],
	personAssignments: {},
	assignmentBoardConfigs: [],
};

export enum BoardStateActions {
	SET_LOADING,
	SET_COMPANIES,
	SET_ASSIGNMENTS,
	SET_PERSON_ASSIGNMENTS,
	SET_ASSIGNMENT_BOARD_CONFIGS,
	UPDATE_PERSON_ASSIGNMENT,
	ADD_PERSON_ASSIGNMENT,
	UPDATE_ASSIGNMENT_BOARD_CONFIG,
	ADD_ASSIGNMENT_BOARD_CONFIG,
	DELETE_PERSON_ASSIGNMENT,
}

/**
 * Updates only the state, API calls are handled in the component
 * @param state
 * @param action
 */
export const BoardStateReducer: Reducer<
	BoardState,
	{ type: BoardStateActions; data: BoardStateData }
> = (state: BoardState, action: { type: BoardStateActions; data: BoardStateData }) => {
	switch (action.type) {
		case BoardStateActions.SET_LOADING:
			return { ...state, loading: action.data.loading };
		case BoardStateActions.SET_ASSIGNMENTS:
			return { ...state, assignments: action.data.assignments };
		case BoardStateActions.SET_COMPANIES:
			return { ...state, companies: action.data.companies };
		case BoardStateActions.SET_PERSON_ASSIGNMENTS:
			return {
				...state,
				personAssignments: {
					...state.personAssignments,
					...action.data.personAssignments,
				},
			};
		case BoardStateActions.SET_ASSIGNMENT_BOARD_CONFIGS:
			return {
				...state,
				assignmentBoardConfigs: action.data.assignmentBoardConfigs,
			};

		case BoardStateActions.UPDATE_PERSON_ASSIGNMENT:
			//Switch person assignments
			const updatePersonAssignments = [
				...state.personAssignments[action.data.assignment.id],
				action.data.newPersonAssignment,
			].filter((pa) => pa.id !== action.data.oldPersonAssignment.id);

			const updatedPersonAssignmentState = { ...state.personAssignments };
			updatedPersonAssignmentState[action.data.assignment.id] =
				updatePersonAssignments
					.sort(personAssignmentSort)
					.filter(personAssignmentFilter);

			return { ...state, personAssignments: updatedPersonAssignmentState };

		case BoardStateActions.DELETE_PERSON_ASSIGNMENT:
			const personAssignmentsOldDeleted = [
				...state.personAssignments[action.data.assignment.id],
			].filter((pa) => pa.id !== action.data.oldPersonAssignment.id);

			const personAssignmentsOldDeletedObj = { ...state.personAssignments };
			personAssignmentsOldDeletedObj[action.data.assignment.id] =
				personAssignmentsOldDeleted
					.sort(personAssignmentSort)
					.filter(personAssignmentFilter);
			return { ...state, personAssignments: personAssignmentsOldDeletedObj };

		case BoardStateActions.ADD_PERSON_ASSIGNMENT:
			const newPersonAssignments = [
				...state.personAssignments[action.data.assignment.id],
				action.data.newPersonAssignment,
			];

			const newPersonAssignmentState = { ...state.personAssignments };
			newPersonAssignmentState[action.data.assignment.id] = newPersonAssignments
				.sort(personAssignmentSort)
				.filter(personAssignmentFilter);

			return { ...state, personAssignments: newPersonAssignmentState };

		case BoardStateActions.UPDATE_ASSIGNMENT_BOARD_CONFIG:
			// Replace config for this assignment (filter by assignment, not id, to support optimistic updates)
			const updateBoardConfigs = [
				...state.assignmentBoardConfigs.filter(
					(c) =>
						c.assignment !== action.data.newAssignmentBoardConfig.assignment
				),
				action.data.newAssignmentBoardConfig,
			];

			return { ...state, assignmentBoardConfigs: updateBoardConfigs };

		case BoardStateActions.ADD_ASSIGNMENT_BOARD_CONFIG:
			//Switch assignment board config
			const newBoardConfig = [
				...state.assignmentBoardConfigs,
				action.data.newAssignmentBoardConfig,
			];

			return { ...state, assignmentBoardConfigs: newBoardConfig };

		default:
			throw new Error("Unknown action type");
	}
};

/**
 * Generic context for helper functions etc
 */
const BoardContext = createContext<{
	fetchPersonAssignments: (assignments?: AssignmentType[]) => void;
	fetchAssignmentBoardConfigs: () => void;
}>(null);

const BoardStateContext = createContext<BoardState>(null);

export const BoardStateDispatchContext =
	createContext<
		Dispatch<{
			type: BoardStateActions;
			data: BoardStateData;
		}>
	>(null);

export const BoardStateProvider = ({ children }: { children: ReactNode }) => {
	const [boardState, dispatch] = useReducer(BoardStateReducer, initialBoardState);

	// POLL BOARD DATA EVERY 15 MINUTES
	useInterval(() => {
		console.debug("Polled data");
		fetchBoardData(false);
	}, ONE_HOUR_IN_MILLISECONDS / 4);

	useEffect(() => {
		fetchBoardData();
	}, []);

	useEffect(() => {
		console.debug("Global board state updated:", boardState);
	}, [boardState]);

	const fetchBoardData = (loading = true) => {
		dispatch({
			type: BoardStateActions.SET_LOADING,
			data: { loading: loading },
		});
		getAssignments()
			.then(async (res) => {
				return res.filter(assignmentFilter).sort(assignmentSort);
			})
			.then(async (assignments) => {
				// Dispatch assignments to global var
				dispatch({
					type: BoardStateActions.SET_ASSIGNMENTS,
					data: {
						assignments: assignments,
					},
				});
				dispatch({
					type: BoardStateActions.SET_LOADING,
					data: { loading: false },
				});
				fetchCompanies(assignments);
				// PersonAssignments are fetched by boardview.tsx based on URL params
				// to avoid fetching ALL when only viewing a single assignment
				fetchAssignmentBoardConfigs();
			})
			// The poll's next tick retries a network failure; the first load has no
			// next tick, so its network failure keeps reporting.
			.catch(loading ? skipTransientApiErrors : skipFailedPollTick);
	};

	/**
	 * Fetches person assignments for given assignments, handling pagination automatically
	 */
	const fetchPersonAssignments = async (assignments?: AssignmentType[]) => {
		if (assignments === undefined) {
			assignments = boardState.assignments;
		}

		const assignmentIds = assignments.map((a) => a.id);
		// Pagination is handled internally by getPersonAssignments (limit=100 for testing)
		const results = await getPersonAssignments({ assignmentIds, limit: 100 });

		// Group results by assignment ID
		const personAssignments: { [key: number]: PersonAssignmentRetrieveType[] } = {
			...boardState.personAssignments,
		};

		// Initialize empty arrays for all assignments (in case some have no PersonAssignments)
		for (const id of assignmentIds) {
			personAssignments[id] = [];
		}

		// Group the results by assignment
		for (const pa of results) {
			if (pa.assignment in personAssignments) {
				personAssignments[pa.assignment].push(pa);
			}
		}

		// Sort and filter each group
		for (const id of assignmentIds) {
			personAssignments[id] = personAssignments[id]
				.sort(personAssignmentSort)
				.filter(personAssignmentFilter);
		}

		dispatch({
			type: BoardStateActions.SET_PERSON_ASSIGNMENTS,
			data: { personAssignments },
		});
	};

	/**
	 * Not be used as initial fetch, only for updating person assignments
	 */
	const fetchAssignmentBoardConfigs = async () => {
		const assignmentBoardConfigs = await getAssignmentBoardConfigs();
		dispatch({
			type: BoardStateActions.SET_ASSIGNMENT_BOARD_CONFIGS,
			data: { assignmentBoardConfigs: assignmentBoardConfigs.results },
		});
	};

	const fetchCompanies = async (assignments?: AssignmentType[]) => {
		if (assignments === undefined) {
			assignments = boardState.assignments;
		}

		const uniqueCompanyIds = [
			...new Set(assignments.map((a) => a.company).filter(Boolean)),
		];
		const companies = await getCompaniesForIds(uniqueCompanyIds);

		dispatch({
			type: BoardStateActions.SET_COMPANIES,
			data: {
				companies: companies.sort(companySort),
			},
		});
	};

	return (
		<BoardContext.Provider
			value={{ fetchPersonAssignments, fetchAssignmentBoardConfigs }}
		>
			<BoardStateContext.Provider value={boardState}>
				<BoardStateDispatchContext.Provider value={dispatch}>
					{children}
				</BoardStateDispatchContext.Provider>
			</BoardStateContext.Provider>
		</BoardContext.Provider>
	);
};

export const useBoardContext = () => {
	return useContext(BoardContext);
};

/**
 * Board state context
 * @returns Context containing board state
 */
export const useBoardState = () => {
	return useContext(BoardStateContext);
};

/**
 * Board state dispatcher context
 * @returns Context containing dispatcher function for board state (used to update state)
 */
export const useBoardStateDispatch = () => {
	return useContext(BoardStateDispatchContext);
};
